import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    MenuItem,
    IconButton,
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress,
    Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { getAllMonthlyTargetsAction, createActivityAction, clearCreateActivityAction, getAllVillagesAction, updateActivityAction, getAllDistrictsAction, getAllMandalsAction, createVillageAction, clearCreateVillageAction, createMandalAction, clearCreateMandalAction } from "../../../store/actions";

// ─── Create Village Dialog ───────────────────────────────────────────────────
const CreateVillageDialog = ({ open, onClose, onSuccess, userId }) => {
    const dispatch = useDispatch();

    const { successData: districtsData } = useSelector((s) => s.getAllDistrictsReducer || {});
    const { successData: mandalsData }   = useSelector((s) => s.getAllMandalsReducer || {});
    const { successData: createVillageSuccess, loading: createVillageLoading, error: createVillageError, errorInfo: createVillageErrorInfo } = useSelector((s) => s.createVillageReducer || {});
    const { successData: createMandalSuccess, loading: createMandalLoading, error: createMandalError, errorInfo: createMandalErrorInfo } = useSelector((s) => s.createMandalReducer || {});

    const [districtId, setDistrictId]   = useState('');
    const [mandalText, setMandalText]         = useState('');
    const [selectedMandalId, setSelectedMandalId] = useState('');
    const [mandalFocused, setMandalFocused]   = useState(false);
    const [villageName, setVillageName] = useState('');
    const [nameError,   setNameError]   = useState('');
    const [pendingVillageCreate, setPendingVillageCreate] = useState(false);

    // Helper: extract mandal's incharge user id regardless of field name variant
    const getMandalInchargeId = (m) =>
        m.inchargeUserId ?? m.incharge_user_id ?? m.mandalInchargeUserId ?? m.inchargeUser?.userId ?? null;

    // Helper: extract mandal's district id regardless of field name variant
    const getMandalDistrictId = (m) =>
        m.districtId ?? m.district_id ?? m.districtID ?? null;

    const allMandals = useMemo(() => mandalsData?.data?.mandals || [], [mandalsData]);
    const allDistricts = useMemo(() => districtsData?.data?.districts || [], [districtsData]);

    // All mandals for selected district (used as suggestions)
    const mandalSuggestions = useMemo(() =>
        districtId
            ? allMandals.filter(m => String(getMandalDistrictId(m)) === String(districtId))
            : []
    , [allMandals, districtId]);

    // Filtered suggestions matching typed text
    const matchingMandals = useMemo(() =>
        mandalText.trim()
            ? mandalSuggestions.filter(m =>
                (m.name || m.mandalName || m.mandal_name || '').toLowerCase().includes(mandalText.toLowerCase().trim())
              )
            : mandalSuggestions
    , [mandalSuggestions, mandalText]);

    // Kept for district auto-select only
    const userMandals = useMemo(() => {
        if (!userId) return allMandals;
        const filtered = allMandals.filter(m => String(getMandalInchargeId(m)) === String(userId));
        return filtered.length > 0 ? filtered : allMandals;
    }, [allMandals, userId]);

    // User's districts (derived from user's mandals)
    const userDistricts = useMemo(() => {
        const districtIds = new Set(
            userMandals.map(m => getMandalDistrictId(m)).filter(Boolean).map(String)
        );
        if (districtIds.size === 0) return allDistricts;
        const filtered = allDistricts.filter(d => districtIds.has(String(d.districtId)));
        return filtered.length > 0 ? filtered : allDistricts;
    }, [allDistricts, userMandals]);

    // Auto-select district when only one
    useEffect(() => {
        if (!open) return;
        if (userDistricts.length === 1) setDistrictId(String(userDistricts[0].districtId));
    }, [open, userDistricts]);

    // Reset mandal search when district changes
    useEffect(() => {
        setMandalText('');
        setSelectedMandalId('');
    }, [districtId]);

    // Fetch reference data on open
    useEffect(() => {
        if (open) {
            dispatch(getAllDistrictsAction());
            dispatch(getAllMandalsAction());
        }
    }, [open, dispatch]);

    // Handle mandal creation → then create village
    useEffect(() => {
        if (!open) return;
        if (createMandalSuccess && pendingVillageCreate) {
            const newMandalId = createMandalSuccess?.data?.mandalId
                || createMandalSuccess?.data?.id
                || createMandalSuccess?.mandalId;
            if (newMandalId) {
                dispatch(clearCreateMandalAction());
                dispatch(getAllMandalsAction());
                dispatch(createVillageAction({ name: villageName.trim(), mandalId: Number(newMandalId) }));
                setPendingVillageCreate(false);
            }
        }
        if (createMandalError && pendingVillageCreate) {
            toast.error(createMandalErrorInfo || 'Failed to create mandal');
            dispatch(clearCreateMandalAction());
            setPendingVillageCreate(false);
        }
    }, [open, createMandalSuccess, createMandalError]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle village creation result
    useEffect(() => {
        if (!open) return;
        if (createVillageSuccess) {
            toast.success('Village created successfully!');
            dispatch(clearCreateVillageAction());
            dispatch(getAllVillagesAction());
            const newId = createVillageSuccess?.data?.villageId || createVillageSuccess?.data?.id || createVillageSuccess?.villageId;
            onSuccess?.(newId);
            handleClose();
        }
        if (createVillageError) {
            toast.error(createVillageErrorInfo || 'Failed to create village');
            dispatch(clearCreateVillageAction());
        }
    }, [open, createVillageSuccess, createVillageError]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClose = () => {
        setDistrictId(''); setMandalText(''); setSelectedMandalId('');
        setVillageName(''); setNameError(''); setPendingVillageCreate(false);
        onClose();
    };

    const handleSubmit = () => {
        if (!villageName.trim()) { setNameError('Village name is required'); return; }
        if (!mandalText.trim()) { toast.error('Please enter or select a mandal'); return; }
        if (!districtId)        { toast.error('Please select a district'); return; }
        setNameError('');
        if (selectedMandalId) {
            // Existing mandal selected — create village directly
            dispatch(createVillageAction({ name: villageName.trim(), mandalId: Number(selectedMandalId) }));
        } else {
            // New mandal name typed — create mandal first, then village
            setPendingVillageCreate(true);
            dispatch(createMandalAction({ name: mandalText.trim(), districtId: Number(districtId), inchargeUserId: null }));
        }
    };

    const singleDistrict = userDistricts.length === 1;
    const loading = createVillageLoading || createMandalLoading;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
            slotProps={{ paper: { sx: { borderRadius: 4, p: { xs: 2, sm: 3 } } } }}>
            <IconButton onClick={handleClose} sx={{ position: 'absolute', top: 12, right: 12 }}>
                <CloseIcon />
            </IconButton>
            <DialogTitle sx={{ p: 0, mb: 2 }}>
                <Typography fontSize={22} fontWeight={700}>Create Village</Typography>
                <Typography fontSize={13} color="text.secondary">Fill in the details below</Typography>
            </DialogTitle>
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* District */}
                <Box>
                    <Typography fontSize={13} fontWeight={600} mb={0.5}>District</Typography>
                    {singleDistrict ? (
                        <TextField
                            fullWidth size="small" value={userDistricts[0]?.name || ''}
                            disabled
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F3F4F6' } }}
                        />
                    ) : (
                        <Autocomplete
                            options={userDistricts}
                            getOptionLabel={(o) => o.name || ''}
                            isOptionEqualToValue={(o, v) => o.districtId === v.districtId}
                            value={userDistricts.find(d => String(d.districtId) === String(districtId)) || null}
                            onChange={(_, val) => { setDistrictId(val ? String(val.districtId) : ''); setMandalText(''); setSelectedMandalId(''); }}
                            renderInput={(params) => (
                                <TextField {...params} size="small" placeholder="Select district"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                            )}
                        />
                    )}
                </Box>

                {/* Mandal — search or create */}
                <Box>
                    <Typography fontSize={13} fontWeight={600} mb={0.5}>Mandal</Typography>
                    <Box sx={{ position: 'relative' }}>
                        <TextField
                            fullWidth size="small"
                            placeholder={districtId ? 'Search or create mandal' : 'Select district first'}
                            value={mandalText}
                            onChange={(e) => { setMandalText(e.target.value); setSelectedMandalId(''); }}
                            onFocus={() => setMandalFocused(true)}
                            onBlur={() => setTimeout(() => setMandalFocused(false), 200)}
                            disabled={!districtId}
                            helperText={
                                selectedMandalId
                                    ? '✓ Existing mandal selected'
                                    : mandalText.trim()
                                    ? 'New mandal will be created'
                                    : ''
                            }
                            FormHelperTextProps={{
                                sx: { color: selectedMandalId ? 'success.main' : 'info.main' }
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        {mandalFocused && matchingMandals.length > 0 && (
                            <Paper elevation={4} sx={{ position: 'absolute', zIndex: 1400, width: '100%', maxHeight: 160, overflow: 'auto', mt: 0.5, borderRadius: 1 }}>
                                {matchingMandals.map((m) => (
                                    <Box
                                        key={m.mandalId || m.id}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => {
                                            const name = m.name || m.mandalName || m.mandal_name || '';
                                            setMandalText(name);
                                            setSelectedMandalId(String(m.mandalId || m.id));
                                            setMandalFocused(false);
                                        }}
                                        sx={{ py: 0.75, px: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#F3F6F9' } }}
                                    >
                                        <Typography variant="body2">{m.name || m.mandalName || m.mandal_name || ''}</Typography>
                                    </Box>
                                ))}
                            </Paper>
                        )}
                    </Box>
                </Box>

                {/* Village Name */}
                <Box>
                    <Typography fontSize={13} fontWeight={600} mb={0.5}>Village Name</Typography>
                    <TextField
                        fullWidth size="small" placeholder="Enter village name"
                        value={villageName}
                        onChange={(e) => { setVillageName(e.target.value); if (nameError) setNameError(''); }}
                        error={Boolean(nameError)}
                        helperText={nameError}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </Box>

                {/* Actions */}
                <Box display="flex" gap={2} mt={1}>
                    <Button fullWidth variant="outlined" onClick={handleClose} disabled={createVillageLoading}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, color: '#000', border: '1px solid #000' }}>
                        Cancel
                    </Button>
                    <Button fullWidth variant="contained" onClick={handleSubmit} disabled={createVillageLoading}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#5B4CF5', '&:hover': { bgcolor: '#4a3de0' } }}>
                        {createVillageLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create'}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};
// ─────────────────────────────────────────────────────────────────────────────

// Validation Schema
const validationSchema = Yup.object({
    pre_target_id: Yup.number()
        .required("Pre-target ID is required")
        .positive("Must be a positive number")
        .integer("Must be an integer"),
    pre_month_id: Yup.number()
        .required("Pre-month ID is required")
        .positive("Must be a positive number")
        .integer("Must be an integer"),
    village_id: Yup.number()
        .required("Village ID is required")
        .positive("Must be a positive number")
        .integer("Must be an integer"),
    activity_name: Yup.string()
        .required("Activity name is required")
        .oneOf(["VLM", "MVLM"], "Activity must be either VLM or MVLM"),
    start_time: Yup.string()
        .required("Start time is required")
        .matches(/^([0-1]?\d|2[0-3]):[0-5]\d$/, "Invalid time format"),
    end_time: Yup.string()
        .required("End time is required")
        .matches(/^([0-1]?\d|2[0-3]):[0-5]\d$/, "Invalid time format")
        .test("is-greater", "End time must be after start time", function (value) {
            const { start_time } = this.parent;
            if (!start_time || !value) return true;
            const [startHour, startMin] = start_time.split(":").map(Number);
            const [endHour, endMin] = value.split(":").map(Number);
            const startMinutes = startHour * 60 + startMin;
            const endMinutes = endHour * 60 + endMin;
            return endMinutes > startMinutes;
        }),
    no_of_farmers: Yup.number()
        .required("Number of farmers is required")
        .positive("Must be a positive number")
        .integer("Must be an integer")
        .min(1, "Must be at least 1"),
});

const Activity = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const editData = location.state?.editData;
    const autoPreTargetId = location.state?.preTargetId ?? null;
    const autoMonthId = location.state?.monthId ?? null;
    const [addVillageOpen, setAddVillageOpen] = useState(false);

    const userDetails = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("userDetails"));
        } catch {
            return null;
        }
    }, []);
    const userRole = userDetails?.role;

    // Media state for local files and captured images
    const [mediaFiles, setMediaFiles] = React.useState([]);
    const { successData: monthlyTargetsData, loading: monthlyTargetsLoading, error: monthlyTargetsError } = useSelector(
        (state) => state.getAllMonthlyTargetsReducer || {}
    );
    const { successData: villagesData, loading: villagesLoading } = useSelector(
        (state) => state.getAllVillagesReducer || {}
    );
    const { successData: createActivitySuccess, error: createActivityError, loading: createActivityLoading } = useSelector(
        (state) => state.createActivityReducer || {}
    );

    const { successData: updateActivitySuccess, error: updateActivityError, loading: updateActivityLoading } = useSelector(
        (state) => state.updateActivityReducer || {}
    );

    // Parse data
    const monthlyTargetsList = useMemo(() => {
        const data = monthlyTargetsData?.data?.monthlyTargets
            || monthlyTargetsData?.data
            || monthlyTargetsData || [];
        return Array.isArray(data) ? data : [];
    }, [monthlyTargetsData]);

    const villagesList = useMemo(() => {
        const allVillages = villagesData?.data?.villages || [];
        if (userRole === "MDO" && userDetails?.id) {
            return allVillages.filter(v => v.mandalInchargeUserId === userDetails.id);
        }
        return allVillages;
    }, [villagesData, userRole, userDetails]);

    // Get unique pre_target_ids for dropdown
    const uniquePreTargets = useMemo(() => {
        const uniqueIds = [...new Set(monthlyTargetsList.map(mt => mt.pre_target_id).filter(id => id != null))];
        const result = uniqueIds.map(id => ({
            pre_target_id: id,
            label: `Pre-Target ID: ${id}`
        }));
        return result;
    }, [monthlyTargetsList]);

    const formik = useFormik({
        initialValues: {
            pre_target_id: "",
            pre_month_id: "",
            village_id: "",
            activity_name: "",
            start_time: "",
            end_time: "",
            no_of_farmers: "",
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            // Helper function to convert time (HH:MM) to ISO 8601 datetime string
            const timeToISO = (timeString) => {
                const today = new Date();
                const [hours, minutes] = timeString.split(":");
                today.setHours(Number.parseInt(hours, 10));
                today.setMinutes(Number.parseInt(minutes, 10));
                today.setSeconds(0);
                today.setMilliseconds(0);
                return today.toISOString();
            };

            // Prepare FormData for API (append each field as string)
            const formData = new FormData();
            formData.append("pre_target_id", String(values.pre_target_id));
            formData.append("pre_month_id", String(values.pre_month_id));
            formData.append("village_id", String(values.village_id));
            formData.append("activity_name", values.activity_name);
            formData.append("start_time", timeToISO(values.start_time));
            formData.append("end_time", timeToISO(values.end_time));
            formData.append("no_of_farmers", String(values.no_of_farmers));

            // Append each media file (actual file, not JSON)
            mediaFiles.forEach((fileObj, idx) => {
                if (fileObj.file) {
                    formData.append("media", fileObj.file, fileObj.name);
                }
            });

            if (editData?.id) {
                dispatch(updateActivityAction({ id: editData.id, data: formData }));
            } else {
                dispatch(createActivityAction(formData));
            }
        },
    });

    // Debug button state
    useEffect(() => {
        console.log('Button disabled state:', {
            isSubmitting: formik.isSubmitting,
            createActivityLoading: createActivityLoading,
            disabled: formik.isSubmitting || !!createActivityLoading,
            formValues: formik.values,
            formErrors: formik.errors,
            isValid: formik.isValid
        });
    }, [formik.isSubmitting, createActivityLoading, formik.values, formik.errors, formik.isValid]);

    // Fetch data on mount
    useEffect(() => {
        dispatch(getAllMonthlyTargetsAction());
        dispatch(getAllVillagesAction());
    }, [dispatch]);

    // Auto-fill pre_target_id and pre_month_id when navigated from viewPreActivity
    useEffect(() => {
        if (!editData && autoPreTargetId && monthlyTargetsList.length > 0) {
            formik.setFieldValue('pre_target_id', autoPreTargetId);
            if (autoMonthId) {
                formik.setFieldValue('pre_month_id', autoMonthId);
            }
        }
    }, [autoPreTargetId, autoMonthId, monthlyTargetsList]); 

    // Populate form when editData is present
    useEffect(() => {
        if (editData) {
            // Helper function to convert ISO datetime to HH:MM
            const isoToTime = (isoString) => {
                const date = new Date(isoString);
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                return `${hours}:${minutes}`;
            };

            formik.setValues({
                pre_target_id: editData.preTargetId || editData.pre_target_id || "",
                pre_month_id: editData.preMonthId || editData.pre_month_id || "",
                village_id: editData.villageId || editData.village_id || "",
                activity_name: editData.activityName || editData.activity_name || "",
                start_time: isoToTime(editData.startTime || editData.start_time),
                end_time: isoToTime(editData.endTime || editData.end_time),
                no_of_farmers: editData.noOfFarmers || editData.no_of_farmers || "",
            });
        }
    }, [editData]);


    // Handle create success
    useEffect(() => {
        if (createActivitySuccess) {
            toast.success("Activity created successfully!");
            dispatch(clearCreateActivityAction());
            navigate(-1);
        }
    }, [createActivitySuccess, dispatch, navigate]);

    // Handle update success
    useEffect(() => {
        if (updateActivitySuccess) {
            toast.success("Activity updated successfully!");
            navigate(-1);
        }
    }, [updateActivitySuccess, navigate]);

    // Handle create error
    useEffect(() => {
        if (createActivityError) {
            toast.error(createActivityError?.message || "Failed to create activity");
            dispatch(clearCreateActivityAction());
        }
    }, [createActivityError, dispatch]);

    // Handle update error
    useEffect(() => {
        if (updateActivityError) {
            toast.error(updateActivityError?.message || "Failed to update activity");
        }
    }, [updateActivityError]);

    // Handle monthly targets fetch error
    useEffect(() => {
        if (monthlyTargetsError) {
            toast.error("Failed to load monthly targets");
        }
    }, [monthlyTargetsError]);

    // Filter monthly targets by selected pre_target_id
    const filteredMonthlyTargets = formik.values.pre_target_id
        ? monthlyTargetsList.filter(mt => mt.pre_target_id === formik.values.pre_target_id)
        : [];

    return (
        <Box sx={{ overflowX: 'hidden', width: '100%', boxSizing: 'border-box' }}>
            <Box
                sx={{
                    background: "#FFFFFF",
                    borderRadius: 3,
                    p: { xs: 1.5, sm: 2 },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    mt: { xs: 8, sm: 0, md: 0 },
                    boxSizing: 'border-box',
                    overflowX: 'hidden',
                }}
            >
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={3}>
                    <Typography
                        fontWeight={600}
                        sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem', md: '2rem' }, flex: 1, minWidth: 0, wordBreak: 'break-word' }}
                    >
                        {editData ? "Edit Pre-Season Activity" : "Pre-Season Activity Information"}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                        <IconButton onClick={() => navigate('/SOorMDO/preActivity')}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Form */}
                <form onSubmit={formik.handleSubmit}>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                        {/* Pre-Target ID and Month fields are hidden but values are kept in formik and sent in payload */}

                            {/* Village ID */}
                        <Grid item size={{ xs: 12, sm: 6 }}>
                            <Box display="flex" alignItems="flex-start" gap={1}>
                                <Autocomplete
                                    fullWidth
                                    options={villagesList}
                                    loading={villagesLoading}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                            backgroundColor: "#FAFAFA",
                                        },
                                    }}
                                    value={villagesList.find(v => (v.villageId || v.id) === formik.values.village_id) || null}
                                    onChange={(e, val) => formik.setFieldValue("village_id", val ? (val.villageId || val.id) : "")}
                                    getOptionLabel={(option) => option.name || option.village_name || option.villageName || ""}
                                    isOptionEqualToValue={(option, value) => (option.villageId || option.id) === (value.villageId || value.id)}
                                    clearOnEscape
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            size="small"
                                            label="Village"
                                            name="village_id"
                                            onBlur={() => formik.setFieldTouched("village_id", true)}
                                            error={formik.touched.village_id && Boolean(formik.errors.village_id)}
                                            helperText={formik.touched.village_id && formik.errors.village_id}
                                        />
                                    )}
                                />
                                <Button
                                    variant="contained"
                                    size="small"
                                    sx={{ minWidth: 36, px: 1, py: 0.9, fontWeight: 700, fontSize: 18, borderRadius: 2, flexShrink: 0, mt: 0.25 }}
                                    onClick={() => setAddVillageOpen(true)}
                                >
                                    +
                                </Button>
                            </Box>
                            {/* Create Village Dialog */}
                            <CreateVillageDialog
                                open={addVillageOpen}
                                onClose={() => setAddVillageOpen(false)}
                                userId={userDetails?.id}
                                onSuccess={(newVillageId) => {
                                    if (newVillageId) formik.setFieldValue('village_id', newVillageId);
                                    setAddVillageOpen(false);
                                }}
                            />
                        </Grid>

                        {/* Activity Name */}
                        <Grid item size={{ xs: 12, sm: 6 }}>
                            <TextField
                                size="small"
                                select
                                fullWidth
                                label="Activity Name"
                                name="activity_name"
                                value={formik.values.activity_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.activity_name && Boolean(formik.errors.activity_name)}
                                helperText={formik.touched.activity_name && formik.errors.activity_name}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                        backgroundColor: "#FAFAFA",
                                    },
                                }}
                            >
                                <MenuItem value="">Select Activity</MenuItem>
                                <MenuItem value="VLM">VLM</MenuItem>
                                <MenuItem value="MVLM">MVLM</MenuItem>
                            </TextField>
                        </Grid>

                        {/* Start Time */}
                        <Grid item size={{ xs: 12, sm: 4 }}>
                            <TextField
                                size="small"
                                fullWidth
                                label="Start Time"
                                name="start_time"
                                type="time"
                                value={formik.values.start_time}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.start_time && Boolean(formik.errors.start_time)}
                                helperText={formik.touched.start_time && formik.errors.start_time}
                                slotProps={{
                                    inputLabel: { shrink: true },
                                    input: { step: 300 },
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                        backgroundColor: "#FAFAFA",
                                    },
                                }}
                            />
                        </Grid>

                        {/* End Time */}
                        <Grid item size={{ xs: 12, sm: 4 }}>
                            <TextField
                                size="small"
                                fullWidth
                                label="End Time"
                                name="end_time"
                                type="time"
                                value={formik.values.end_time}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.end_time && Boolean(formik.errors.end_time)}
                                helperText={formik.touched.end_time && formik.errors.end_time}
                                slotProps={{
                                    inputLabel: { shrink: true },
                                    input: { step: 300 }, // 5 min intervals
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                        backgroundColor: "#FAFAFA",
                                    },
                                }}
                            />
                        </Grid>

                        {/* Number of Farmers */}
                        <Grid item size={{ xs: 12, sm: 4 }}>
                            <TextField
                                size="small"
                                fullWidth
                                label="Number of Farmers"
                                name="no_of_farmers"
                                value={formik.values.no_of_farmers}
                                onChange={(e) => {
                                const value = e.target.value;
                                if (/^\d*$/.test(value)) {   
                                formik.setFieldValue("no_of_farmers", value);
                                }
                                }}
                                onBlur={formik.handleBlur}
                                error={formik.touched.no_of_farmers && Boolean(formik.errors.no_of_farmers)}
                                helperText={formik.touched.no_of_farmers && formik.errors.no_of_farmers}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                        backgroundColor: "#FAFAFA",
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid container mt={4}>
                        {/* Media Upload & Capture */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" fontWeight={600} mb={1}>Upload or Capture Images</Typography>
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                capture="environment"
                                onChange={e => {
                                    const files = Array.from(e.target.files);
                                    // Convert files to preview URLs
                                    const fileObjs = files.map(file => ({
                                        file,
                                        name: file.name,
                                        url: URL.createObjectURL(file),
                                        captured_at: new Date().toISOString()
                                    }));
                                    setMediaFiles(prev => [...prev, ...fileObjs]);
                                }}
                            />
                            {/* Show preview gallery */}
                            <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
                                {mediaFiles.map((file, idx) => (
                                    <Box key={file.url || file.name || idx} sx={{ position: "relative" }}>
                                        <img src={file.url} alt={file.name} style={{ width: 100, height: 100, objectFit: "cover", borderRadius: 8 }} />
                                        <IconButton
                                            size="small"
                                            sx={{ position: "absolute", top: 0, right: 0, background: "rgba(255,255,255,0.7)" }}
                                            onClick={() => setMediaFiles(mediaFiles.filter((_, i) => i !== idx))}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Submit Buttons */}
                    <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/SOorMDO/preActivity')}
                            sx={{
                                borderRadius: 2,
                                textTransform: "none",
                                px: 3,
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={formik.isSubmitting || !!createActivityLoading || !!updateActivityLoading}
                            sx={{
                                borderRadius: 2,
                                textTransform: "none",
                                px: 3,
                            }}
                        >
                            {(createActivityLoading || updateActivityLoading)
                                ? (editData ? "Updating..." : "Submitting...")
                                : (editData ? "Update" : "Submit")}
                        </Button>
                    </Box>
                </form>
            </Box>
        </Box>
    );
};

export default Activity;